import api from './api';
import ordenReparacionService from './ordenReparacionService';

export type EstadoTurno = 'PROGRAMADO' | 'RECIBIDO' | 'EN_TRABAJO' | 'FACTURADO';

export type TipoClienteTurno = 'Nuevo' | 'Habitual';

export const ESTADOS_TURNO: EstadoTurno[] = [
  'PROGRAMADO',
  'RECIBIDO',
  'EN_TRABAJO',
  'FACTURADO',
];

export const LABEL_ESTADO_TURNO: Record<EstadoTurno, string> = {
  PROGRAMADO: 'PROGRAMADO',
  RECIBIDO: 'RECIBIDO',
  EN_TRABAJO: 'EN TRABAJO',
  FACTURADO: 'FACTURADO',
};

export interface TurnoServicioDto {
  codigo: string;
  descripcion: string;
}

export interface CreateTurnoServicioDto {
  codigo: string;
  descripcion: string;
}

export interface UpdateTurnoServicioDto {
  descripcion: string;
}

export interface CreateTurnoDto {
  fecha: string;
  tipoCliente: TipoClienteTurno;
  anulado: boolean;
  nroCliente: number | null;
  nombreCliente?: string | null;
  telefonoCliente?: string | null;
  nroVendedor: number | null;
  codigoServicio: string;
  horaInicio: string;
  duracionMinutos: number;
  nota?: string | null;
  /** Identificador de unidad (patente). Opcional. */
  patente?: string | null;
  estado: EstadoTurno;
}

export type UpdateTurnoDto = CreateTurnoDto;

export interface TurnoReadDto extends CreateTurnoDto {
  id: number | string;
  descripcionServicio?: string | null;
  /** NroFormulario de la OT vinculada (tipo 18); null si aún no tiene. */
  nroFormulario?: number | null;
}

export interface TurnoSearchParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  fecha?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  estado?: EstadoTurno;
  signal?: AbortSignal;
}

export interface TurnoPagedResult {
  items: TurnoReadDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const normalizeFechaKey = (value?: string | null): string => {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const str = (v: unknown): string => String(v ?? '').trim();

const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** API a veces manda bool, 0/1 o strings ("true"/"S"). */
const toBool = (v: unknown): boolean => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 's' || s === 'si' || s === 'sí' || s === 'y' || s === 'yes';
  }
  return Boolean(v);
};

const normalizeServicio = (raw: Record<string, unknown>): TurnoServicioDto => ({
  codigo: str(raw.codigo ?? raw.Codigo),
  descripcion: str(raw.descripcion ?? raw.Descripcion),
});

const normalizeTurno = (raw: Record<string, unknown>): TurnoReadDto => {
  const idRaw = raw.id ?? raw.Id ?? raw.nroTurno ?? raw.NroTurno;
  const nroFormulario = numOrNull(raw.nroFormulario ?? raw.NroFormulario);
  return {
    id: idRaw != null && idRaw !== '' ? (idRaw as number | string) : '',
    fecha: normalizeFechaKey(str(raw.fecha ?? raw.Fecha)),
    tipoCliente: (str(raw.tipoCliente ?? raw.TipoCliente) || 'Habitual') as TipoClienteTurno,
    anulado: toBool(raw.anulado ?? raw.Anulado ?? raw.estaAnulado ?? raw.EstaAnulado),
    nroCliente: numOrNull(raw.nroCliente ?? raw.NroCliente),
    nombreCliente: str(raw.nombreCliente ?? raw.NombreCliente) || null,
    telefonoCliente: str(raw.telefonoCliente ?? raw.TelefonoCliente) || null,
    nroVendedor: numOrNull(raw.nroVendedor ?? raw.NroVendedor),
    codigoServicio: str(raw.codigoServicio ?? raw.CodigoServicio),
    descripcionServicio: str(raw.descripcionServicio ?? raw.DescripcionServicio) || null,
    horaInicio: str(raw.horaInicio ?? raw.HoraInicio),
    duracionMinutos: Number(raw.duracionMinutos ?? raw.DuracionMinutos ?? 0) || 0,
    nota: str(raw.nota ?? raw.Nota) || null,
    patente: str(raw.patente ?? raw.Patente) || null,
    estado: (str(raw.estado ?? raw.Estado) || 'PROGRAMADO') as EstadoTurno,
    nroFormulario: nroFormulario != null && nroFormulario > 0 ? nroFormulario : null,
  };
};

const normalizePagedTurnos = (
  data: unknown,
  params: TurnoSearchParams,
): TurnoPagedResult => {
  if (data && typeof data === 'object' && ('items' in data || 'Items' in data)) {
    const paged = data as TurnoPagedResult & {
      Items?: unknown[];
      TotalCount?: number;
      PageNumber?: number;
      PageSize?: number;
      TotalPages?: number;
    };
    const items = (paged.items ?? paged.Items ?? []) as unknown[];
    return {
      items: items.map((item) => normalizeTurno(item as Record<string, unknown>)),
      totalCount: paged.totalCount ?? paged.TotalCount ?? 0,
      pageNumber: paged.pageNumber ?? paged.PageNumber ?? 1,
      pageSize: paged.pageSize ?? paged.PageSize ?? 10,
      totalPages: paged.totalPages ?? paged.TotalPages ?? 0,
    };
  }
  if (Array.isArray(data)) {
    const items = data.map((item) => normalizeTurno(item as Record<string, unknown>));
    return {
      items,
      totalCount: items.length,
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      totalPages: 1,
    };
  }
  return {
    items: [],
    totalCount: 0,
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
    totalPages: 0,
  };
};

const turnoService = {
  getTurnos: async (params: TurnoSearchParams): Promise<TurnoPagedResult> => {
    const { signal, ...query } = params;
    const resp = await api.get('/api/Turnos', {
      params: {
        SearchTerm: query.searchTerm || undefined,
        PageNumber: query.pageNumber ?? 1,
        PageSize: query.pageSize ?? 10,
        Fecha: query.fecha || undefined,
        FechaDesde: query.fechaDesde || undefined,
        FechaHasta: query.fechaHasta || undefined,
        Estado: query.estado || undefined,
      },
      signal,
    });
    return normalizePagedTurnos(resp.data, query);
  },

  getTurno: async (id: number | string): Promise<TurnoReadDto> => {
    const encoded = encodeURIComponent(String(id).trim());
    const resp = await api.get(`/api/Turnos/${encoded}`);
    return normalizeTurno(resp.data as Record<string, unknown>);
  },

  getServicios: async (searchTerm?: string): Promise<TurnoServicioDto[]> => {
    const resp = await api.get('/api/Turnos/servicios', {
      params: { SearchTerm: searchTerm || undefined },
    });
    const data = resp.data;
    const list = Array.isArray(data)
      ? data
      : Array.isArray((data as { items?: unknown[] })?.items)
        ? (data as { items: unknown[] }).items
        : Array.isArray((data as { Items?: unknown[] })?.Items)
          ? (data as { Items: unknown[] }).Items
          : [];
    return list.map((item) => normalizeServicio(item as Record<string, unknown>));
  },

  createServicio: async (dto: CreateTurnoServicioDto): Promise<TurnoServicioDto> => {
    const resp = await api.post('/api/Turnos/servicios', dto);
    return normalizeServicio(resp.data as Record<string, unknown>);
  },

  updateServicio: async (codigo: string, dto: UpdateTurnoServicioDto): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.put(`/api/Turnos/servicios/${encoded}`, dto);
  },

  deleteServicio: async (codigo: string): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.delete(`/api/Turnos/servicios/${encoded}`);
  },

  createTurno: async (dto: CreateTurnoDto): Promise<TurnoReadDto> => {
    const response = await api.post('/api/Turnos', dto);
    return normalizeTurno(response.data as Record<string, unknown>);
  },

  updateTurno: async (id: number | string, dto: UpdateTurnoDto): Promise<void> => {
    const encoded = encodeURIComponent(String(id).trim());
    await api.put(`/api/Turnos/${encoded}`, dto);
  },

  deleteTurno: async (id: number | string): Promise<void> => {
    const encoded = encodeURIComponent(String(id).trim());
    await api.delete(`/api/Turnos/${encoded}`);
  },

  vincularOrdenTrabajo: async (
    id: number | string,
    nroFormulario: number,
  ): Promise<TurnoReadDto> => {
    const encoded = encodeURIComponent(String(id).trim());
    const resp = await api.post(`/api/Turnos/${encoded}/orden-trabajo`, { nroFormulario });
    return normalizeTurno(resp.data as Record<string, unknown>);
  },

  /**
   * Crea una OT (puede ir sin ítems) desde el turno, la vincula y pasa a EN_TRABAJO.
   * Si ya tiene OT, devuelve el turno sin recrear.
   */
  generarOrdenTrabajo: async (id: number | string): Promise<TurnoReadDto> => {
    const turno = await turnoService.getTurno(id);
    if (turno.nroFormulario && turno.nroFormulario > 0) {
      return turno;
    }
    if (!turno.nroCliente || turno.nroCliente <= 0) {
      throw new Error('El turno debe tener un cliente habitual para generar la orden de trabajo.');
    }

    const fecha = turno.fecha || new Date().toISOString().slice(0, 10);
    const detalle =
      turno.nota?.trim() ||
      [turno.descripcionServicio, turno.codigoServicio].filter(Boolean).join(' - ') ||
      `Turno #${turno.id}`;

    const orden = await ordenReparacionService.createOrden({
      nroCliente: turno.nroCliente,
      nombreCliente: turno.nombreCliente || null,
      direccionCliente: null,
      nroDepositoOrigen: null,
      nombreDepositoOrigen: null,
      nroVendedor: turno.nroVendedor,
      nroTransporte: null,
      nombreTransporte: null,
      serie: 'A',
      numero: '',
      fechaEmision: fecha,
      horaEmision: turno.horaInicio || '10:00',
      fechaVence: fecha,
      detalleReparacion: detalle,
      patente: turno.patente || null,
      items: [],
    });

    const nroFormulario =
      orden.nroFormulario ??
      (typeof orden.id === 'number' ? orden.id : Number(orden.id));
    if (!Number.isFinite(nroFormulario) || nroFormulario <= 0) {
      throw new Error('No se pudo obtener el número de la orden de trabajo creada.');
    }

    return turnoService.vincularOrdenTrabajo(id, nroFormulario);
  },
};

export default turnoService;
